// oilib keeps infowindows open by default (infowindow_persist), so close any open
// one when the map surface is clicked. oilib dispatches oimap_map_click on the map container.
const closeInfowindow = (map) => {
    if (map?.infowindow) {
        map.infowindow.close();
        map.infowindow.setPosition?.(null);
    }
};

document.addEventListener("oimap_map_click", (e) => {
    closeInfowindow(window.oi?.loaded?.map?.[e.detail?.map_id]);
});

let closeTimer;

const cancelClose = () => {
    clearTimeout(closeTimer);
};

const scheduleClose = () => {
    cancelClose();
    closeTimer = setTimeout(() => {
        if (document.querySelector(".smart-info-window:hover, .gm-style-iw-c:hover")) {
            return;
        }
        Object.values(window.oi?.loaded?.map || {}).forEach(closeInfowindow);
    }, 250);
};

document.addEventListener("oimap_marker_mouseover", cancelClose);
document.addEventListener("oimap_marker_mouseout", scheduleClose);

document.addEventListener("mouseover", (e) => {
    if (e.target.closest?.(".smart-info-window, .gm-style-iw-c")) {
        cancelClose();
    }
});

document.addEventListener("mouseout", (e) => {
    const area = e.target.closest?.(".oi-map-item, .smart-info-window, .gm-style-iw-c");
    if (area && !area.contains(e.relatedTarget)) {
        scheduleClose();
    }
});

const PLACEMENTS = ["left", "right", "top", "bottom"];

const markerLatLng = (marker) => {
    const position = marker.getPosition ? marker.getPosition() : marker.position;
    if (!position) {
        return null;
    }
    return position instanceof google.maps.LatLng ? position : new google.maps.LatLng(position.lat, position.lng);
};

const isShown = (marker) => {
    if (marker.getVisible && marker.getVisible() === false) {
        return false;
    }
    return marker.getMap ? Boolean(marker.getMap()) : Boolean(marker.map);
};

const overlaps = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const placeClearOfPins = (handle, infowindow) => {
    const el = infowindow._container;
    const projection = infowindow.getProjection?.();
    const position = infowindow.getPosition?.();
    const current = infowindow.getPlacement?.();
    if (!el || !projection || !position || !current || current === "center") {
        return;
    }

    const options = infowindow._options || {};
    const padding = options.padding ?? 30;
    const gap = options.gap ?? 16;
    const tailSize = options.tailSize ?? 10;
    const size = infowindow._getMarkerSize ? infowindow._getMarkerSize() : { above: 0, below: 0, left: 0, right: 0 };
    const anchor = projection.fromLatLngToDivPixel(position);
    const inContainer = projection.fromLatLngToContainerPixel(position);
    const mapDiv = handle.map.getDiv();
    const view = {
        left: anchor.x - inContainer.x,
        top: anchor.y - inContainer.y,
    };
    view.right = view.left + mapDiv.offsetWidth;
    view.bottom = view.top + mapDiv.offsetHeight;

    const pins = (handle.markers || []).filter(isShown).map(markerLatLng).filter((latLng) => latLng && !latLng.equals(position)).map((latLng) => {
        const point = projection.fromLatLngToDivPixel(latLng);
        return {
            left: point.x - size.left,
            right: point.x + size.right,
            top: point.y - size.above,
            bottom: point.y + size.below,
        };
    });
    if (!pins.length) {
        return;
    }

    const rectAt = (left, top, width, height) => ({ left, top, right: left + width, bottom: top + height });
    const hits = (rect) => pins.filter((pin) => overlaps(rect, pin)).length;

    const width = el.offsetWidth;
    let height = el.offsetHeight;
    const shown = rectAt(parseFloat(el.style.left) || 0, parseFloat(el.style.top) || 0, width, height);
    const shownHits = hits(shown);
    if (!shownHits) {
        return;
    }

    el.style.setProperty("--siw-max-height", `${Math.max(mapDiv.offsetHeight - 2 * padding, 0)}px`);
    height = el.offsetHeight;

    const fitsView = (rect) => rect.left >= view.left + padding && rect.right <= view.right - padding && rect.top >= view.top + padding && rect.bottom <= view.bottom - padding;

    const place = (placement) => {
        const sideways = placement === "left" || placement === "right";
        let left;
        let top;
        if (placement === "top") {
            left = anchor.x - width / 2;
            top = anchor.y - size.above - height - gap;
        } else if (placement === "bottom") {
            left = anchor.x - width / 2;
            top = anchor.y + size.below + gap;
        } else if (placement === "left") {
            left = anchor.x - size.left - width - gap;
            top = anchor.y - height / 2;
        } else {
            left = anchor.x + size.right + gap;
            top = anchor.y - height / 2;
        }

        const point = sideways ? anchor.y : anchor.x;
        const span = sideways ? height : width;
        const viewStart = sideways ? view.top : view.left;
        const viewEnd = sideways ? view.bottom : view.right;
        const min = Math.max(point - span + 20 + tailSize, viewStart + padding);
        const max = Math.min(point - tailSize - 20, viewEnd - span - padding);
        const clamp = (value) => Math.min(Math.max(value, min), max);
        const start = clamp(sideways ? top : left);
        const offsets = [start];
        pins.forEach((pin) => {
            offsets.push(sideways ? pin.bottom + 4 : pin.right + 4);
            offsets.push(sideways ? pin.top - span - 4 : pin.left - span - 4);
        });

        const variants = offsets.filter((offset) => offset >= min && offset <= max).sort((a, b) => Math.abs(a - start) - Math.abs(b - start)).map((offset) => {
            const rect = sideways ? rectAt(left, offset, width, height) : rectAt(offset, top, width, height);
            return { placement, rect, fits: fitsView(rect), hits: hits(rect) };
        });
        return variants.find((variant) => variant.fits && !variant.hits) || variants[0];
    };

    const candidates = PLACEMENTS.map(place).filter((candidate) => candidate && candidate.fits);
    const clear = candidates.find((candidate) => !candidate.hits);
    const fewest = candidates.filter((candidate) => candidate.hits < shownHits).sort((a, b) => a.hits - b.hits)[0];
    const choice = clear || fewest;
    if (!choice) {
        return;
    }

    const { placement, rect } = choice;
    const along = placement === "top" || placement === "bottom" ? anchor.x - rect.left : anchor.y - rect.top;
    const span = placement === "top" || placement === "bottom" ? width : height;
    const tail = Math.max(20, Math.min(along - tailSize, span - 20 - 2 * tailSize));
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;
    el.style.setProperty("--siw-tail-offset", `${tail}px`);
    el.setAttribute("data-placement", placement);
    infowindow._placement = placement;
};

const makePinAware = (handle) => {
    const infowindow = handle?.infowindow;
    if (!infowindow || infowindow.pinAware || typeof infowindow.getPlacement !== "function") {
        return;
    }
    infowindow.pinAware = true;
    const originalDraw = infowindow.draw.bind(infowindow);
    infowindow.draw = function () {
        originalDraw();
        placeClearOfPins(handle, infowindow);
    };
};

window.addEventListener("oimap_idle", (e) => {
    makePinAware(window.oi?.loaded?.map?.[e.detail?.map_id]);
});
